<template>
  <div>
    <div
      v-infinite-scroll="onLoadMore"
      :infinite-scroll-disabled="disableScroll"
      infinite-scroll-distance="100"
    >
      <div v-for="item in list" :key="item.id" class="mb-4">
        <v-card outlined class="pa-4 audit-log-card" @click="toggle(item.id)">
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="subtitle-2 grey--text text--darken-1">
                {{ formatDate(item.created_at) }}
                <span v-if="getCauserName(item)">
                  - added by
                  <strong class="black--text">{{ getCauserName(item) }}</strong>
                </span>
              </div>
              <div class="body-2 mt-1">
                {{ getDescription(item) }}
              </div>
            </div>
            <div class="d-flex align-center">
              <v-chip v-if="item.subject_type" small outlined class="mr-2">
                {{ item.subject_type }}
              </v-chip>
              <v-chip
                v-if="item.event"
                small
                :color="eventColor(item.event)"
                text-color="white"
              >
                {{ item.event }}
              </v-chip>
            </div>
          </div>

          <!-- Expandable change details -->
          <v-expand-transition>
            <div v-if="expanded === item.id && hasChanges(item)" class="mt-4">
              <v-divider class="mb-3" />
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(change, field) in getChanges(item)"
                      :key="field"
                    >
                      <td class="font-weight-medium">
                        {{ formatField(field) }}
                      </td>
                      <td class="red--text text--lighten-1">
                        {{ formatValue(change.old) }}
                      </td>
                      <td class="green--text text--darken-1">
                        {{ formatValue(change.new) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </div>

    <!-- Empty state -->
    <no-list
      v-if="!list.length && !loading"
      details="No audit log entries found"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import infiniteScroll from 'vue-infinite-scroll'
import NoList from '@/components/elements/NoList'

export default {
  name: 'AuditLogTable',

  directives: {
    infiniteScroll,
  },

  components: {
    NoList,
  },

  props: {
    list: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    listMeta: {
      type: Object,
      default: () => ({ current_page: 0, last_page: 1 }),
    },
  },

  data() {
    return {
      expanded: null,
    }
  },

  computed: {
    disableScroll() {
      return (
        this.loading || this.listMeta.current_page >= this.listMeta.last_page
      )
    },
  },

  methods: {
    onLoadMore() {
      this.$emit('loadMore')
    },

    toggle(id) {
      this.expanded = this.expanded === id ? null : id
    },

    formatDate(date) {
      return dayjs(date).format('DD - MM - YYYY, h:mm a')
    },

    getCauserName(item) {
      if (item.causer) {
        return item.causer.full_name || item.causer.name || item.causer.email
      }
      if (item.user) {
        return item.user.full_name || item.user.name || item.user.email
      }
      return null
    },

    getDescription(item) {
      if (item.activity) return item.activity

      const event = item.event || 'updated'
      const type = item.subject_type || 'record'
      return `${type} was ${event}`
    },

    eventColor(event) {
      const colors = {
        created: 'green',
        updated: 'blue',
        deleted: 'red',
      }
      return colors[event] || 'grey'
    },

    hasChanges(item) {
      if (!item.properties) return false
      return item.properties.old || item.properties.attributes
    },

    getChanges(item) {
      const changes = {}
      const oldValues = item.properties.old || {}
      const newValues = item.properties.attributes || {}

      const allFields = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ])

      allFields.forEach((field) => {
        const oldVal = oldValues[field]
        const newVal = newValues[field]
        if (oldVal !== newVal) {
          changes[field] = { old: oldVal, new: newVal }
        }
      })

      return changes
    },

    formatField(field) {
      return field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },

    formatValue(value) {
      if (value === null || value === undefined) return '-'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
  },
}
</script>

<style scoped>
.audit-log-card {
  cursor: pointer;
  transition: background-color 0.2s;
}
.audit-log-card:hover {
  background-color: #fafafa;
}
</style>
